import React, { useEffect, useState } from "react";
import {
    Breadcrumb,
    Row,
    Col,
    Card,
    notification,
    Button,
    Spin,
    Table,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Switch,
    Popconfirm,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import http from "../../helpers/http";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/admin-users`;

function AdminList() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [resetRecord, setResetRecord] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);
    const [form] = Form.useForm();
    const [resetForm] = Form.useForm();

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const res = await http.get(base_url);
            const data = res?.data || res;
            const list = Array.isArray(data) ? data : (data?.admins || []);
            setAdmins(list);
        } catch (error) {
            notification.error({
                message: "Error fetching admins",
                description: error?.message,
            });
            setAdmins([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            const values = await form.validateFields();
            setCreateLoading(true);
            const res = await http.post(base_url, {
                name: values.name?.trim(),
                email: values.email?.trim().toLowerCase(),
                password: values.password,
            });
            if (res != null) {
                notification.success({ message: "Admin created successfully" });
                form.resetFields();
                setCreateModalOpen(false);
                fetchAdmins();
            }
        } catch (error) {
            if (error.errorFields) return;
            notification.error({
                message: "Failed to create admin",
                description: error?.message,
            });
        } finally {
            setCreateLoading(false);
        }
    };

    const handleStatusChange = async (record, checked) => {
        const newStatus = checked ? "active" : "inactive";
        try {
            const res = await http.patch(`${base_url}/${record._id}/status`, { status: newStatus });
            if (res != null) {
                notification.success({ message: `Admin ${newStatus}` });
                fetchAdmins();
            }
        } catch (error) {
            notification.error({
                message: "Failed to update status",
                description: error?.message,
            });
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await http.delete(`${base_url}/${id}`);
            if (res != null) {
                notification.success({ message: "Admin deleted successfully" });
                fetchAdmins();
            }
        } catch (error) {
            notification.error({
                message: "Failed to delete admin",
                description: error?.message,
            });
        }
    };

    const handleResetPassword = async () => {
        try {
            const values = await resetForm.validateFields();
            setResetLoading(true);
            const res = await http.post(`${API_URL}auth/reset-user-password/${resetRecord._id}`, {
                newPassword: values.password,
            });
            if (res != null) {
                notification.success({ message: "Password reset successfully" });
                resetForm.resetFields();
                setResetModalOpen(false);
                setResetRecord(null);
            }
        } catch (error) {
            if (error.errorFields) return;
            notification.error({
                message: "Failed to reset password",
                description: error?.message,
            });
        } finally {
            setResetLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (name) => name || "—",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (email) => email || "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => {
                const active = status === "active";
                return (
                    <Space>
                        <Switch
                            checked={active}
                            onChange={(checked) => handleStatusChange(record, checked)}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                        <Tag color={active ? "green" : "red"}>
                            {status === "active" ? "Active" : "Inactive"}
                        </Tag>
                    </Space>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            render: (text, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => {
                            setResetRecord(record);
                            setResetModalOpen(true);
                        }}
                    >
                        Reset Password
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this admin?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Breadcrumb
                style={{ margin: "16px 0" }}
                items={[
                    {
                        title: "JagKris",
                    },
                    {
                        title: "Admins",
                    },
                ]}
            />

            <Row>
                <Col span={24}>
                    <Card
                        title="Admin Users"
                        extra={
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalOpen(true)}
                            >
                                Add Admin
                            </Button>
                        }
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 50 }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={admins}
                                rowKey="_id"
                                pagination={{ pageSize: 10 }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title="Add Admin"
                open={createModalOpen}
                onOk={handleCreate}
                onCancel={() => {
                    form.resetFields();
                    setCreateModalOpen(false);
                }}
                confirmLoading={createLoading}
                okText="Create"
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: "Please enter name" }]}
                    >
                        <Input placeholder="Full name" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: "Please enter email" },
                            { type: "email", message: "Please enter a valid email" },
                        ]}
                    >
                        <Input type="email" placeholder="admin@example.com" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                            { required: true, message: "Please enter password" },
                            { min: 6, message: "Password must be at least 6 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Minimum 6 characters" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Reset Password for ${resetRecord?.name || "Admin"}`}
                open={resetModalOpen}
                onOk={handleResetPassword}
                onCancel={() => {
                    resetForm.resetFields();
                    setResetModalOpen(false);
                    setResetRecord(null);
                }}
                confirmLoading={resetLoading}
                okText="Reset"
                destroyOnClose
            >
                <Form form={resetForm} layout="vertical" style={{ marginTop: 16 }}>
                    <p>Enter a new password for <b>{resetRecord?.email}</b></p>
                    <Form.Item
                        name="password"
                        label="New Password"
                        rules={[
                            { required: true, message: "Please enter new password" },
                            { min: 6, message: "Password must be at least 6 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Minimum 6 characters" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default AdminList;
